import type { EditableCellType } from "./EditableTableCell";
import type { SelectOption } from "../../Select";

/**
 * Parse a currency string to a number using a rightmost-separator heuristic:
 * the last `.` or `,` is treated as the decimal separator, the other is grouping.
 * Strips the currency symbol before parsing.
 */
export function filterCurrencyChars(value: string): string {
  return value.replace(/[^0-9.,]/g, "");
}

export function commitCurrencyInput(value: string, symbol = "$"): string {
  const n = parseCurrencyInput(value, symbol);
  return isNaN(n) ? "" : String(n);
}

export function parseCurrencyInput(text: string, symbol = "$"): number {
  const s = text.split(symbol).join("").trim();
  if (!s) return NaN;
  const lastComma = s.lastIndexOf(","),
    lastDot = s.lastIndexOf(".");
  if (lastComma > lastDot) return parseFloat(s.split(".").join("").replace(",", "."));
  return parseFloat(s.split(",").join(""));
}

/**
 * Coerce a pasted string to match the target cell type.
 * Returns null if coercion is not possible (paste is skipped for that cell).
 */
export function coerceValueForPaste(
  text: string,
  targetType: EditableCellType,
  options?: SelectOption<string>[],
  currencySymbol?: string
): string | number | boolean | null {
  switch (targetType) {
    case "text":
    case "longtext":
      return text;

    case "boolean": {
      const lower = text.toLowerCase().trim();
      if (["true", "1", "yes", "on"].includes(lower)) return true;
      if (["false", "0", "no", "off", ""].includes(lower)) return false;
      return null;
    }

    case "number": {
      const n = parseFloat(text.replace(/[^0-9.-]/g, ""));
      return isNaN(n) ? null : n;
    }

    case "percent": {
      const n = parseFloat(text.replace(/%/g, "").trim());
      return isNaN(n) ? null : n;
    }

    case "currency": {
      const n = parseCurrencyInput(text, currencySymbol ?? "$");
      return isNaN(n) ? null : n;
    }

    case "select": {
      if (!options?.length) return null;
      const exact = options.find(o => o.value === text);
      if (exact) return exact.value;
      const lower = text.toLowerCase().trim();
      const labelMatch = options.find(o => o.label.toLowerCase() === lower);
      if (labelMatch) return labelMatch.value;
      const valueMatch = options.find(o => o.value.toLowerCase() === lower);
      if (valueMatch) return valueMatch.value;
      return null;
    }

    case "multiselect": {
      if (!options?.length) return text;
      const parts = text
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
      const resolved = parts
        .map(p => {
          const exact = options.find(o => o.value === p);
          if (exact) return exact.value;
          const lower = p.toLowerCase();
          const labelMatch = options.find(o => o.label.toLowerCase() === lower);
          if (labelMatch) return labelMatch.value;
          const valueMatch = options.find(o => o.value.toLowerCase() === lower);
          return valueMatch ? valueMatch.value : null;
        })
        .filter((v): v is string => v !== null);
      return resolved.join(",");
    }

    default:
      return text;
  }
}

/**
 * Format a cell value as a string for clipboard copy.
 */
export function formatValueForCopy(
  value: string | number | boolean,
  type: EditableCellType,
  currencySymbol?: string
): string {
  if (type === "boolean") return value ? "true" : "false";
  if (type === "currency" && value !== "")
    return `${currencySymbol ?? "$"}${Number(value).toFixed(2)}`;
  if (type === "percent" && value !== "") return `${value}%`;
  return String(value);
}
